import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css, useTheme } from "styled-components";
import { tombac } from "../../shared/tombac";

export type RippleCoords = { x: number; y: number };

export type RippleProps = {
  boundingRect?: DOMRect;
  coords: RippleCoords;
  phase: "idle" | "hold" | "released" | "canceled";
  pressedKey?: string;
};

export type UseRippleOptions = {
  trigger: HTMLElement | null;
};

export function useRipple({ trigger }: UseRippleOptions) {
  const [rippleProps, setRippleProps] = useState<RippleProps>();
  const hasHoverRef = useRef(false);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      setRippleProps({
        coords: { x: 0, y: 0 },
        pressedKey: event.key,
        phase: "hold",
      });
    }
  }, []);

  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    const target = event.currentTarget as Element;
    const rect = target.getBoundingClientRect();

    setRippleProps((rippleProps) => {
      if (!rippleProps?.pressedKey) return;
      console.log("released");
      return {
        coords: { x: rect.width / 2, y: rect.height / 2 },
        phase: "released",
      };
    });
  }, []);

  const handleMouseDown = useCallback((event: MouseEvent) => {
    const target = event.currentTarget as Element;
    const rect = target.getBoundingClientRect();

    setRippleProps(() => {
      setTimeout(() =>
        setRippleProps((rippleProps) => {
          if (rippleProps?.phase !== "idle") return rippleProps;
          return { ...rippleProps, phase: "hold" };
        })
      );
      return {
        boundingRect: rect,
        coords: {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        },
        phase: "idle", // set "idle" first to trigger "hold" animation in the next tick
      };
    });
  }, []);

  const handleMouseUp = useCallback((event: MouseEvent) => {
    setRippleProps((rippleProps) => {
      const rect = rippleProps?.boundingRect;

      if (!rect) return rippleProps;

      return {
        ...rippleProps,
        boundingRect: undefined,
        coords: {
          x: hasHoverRef.current ? event.clientX - rect.left : 0,
          y: hasHoverRef.current ? event.clientY - rect.top : 0,
        },
        phase: hasHoverRef.current ? "released" : "canceled",
      };
    });
  }, []);

  const handleMouseEnter = useCallback(() => {
    hasHoverRef.current = true;
  }, []);

  const handleMouseLeave = useCallback(() => {
    hasHoverRef.current = false;
  }, []);

  useEffect(() => {
    if (!trigger) return;
    trigger.addEventListener("keydown", handleKeyDown);
    trigger.addEventListener("keyup", handleKeyUp);
    trigger.addEventListener("mousedown", handleMouseDown);
    trigger.addEventListener("mouseenter", handleMouseEnter);
    trigger.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      trigger.removeEventListener("keydown", handleKeyDown);
      trigger.removeEventListener("keyup", handleKeyUp);
      trigger.removeEventListener("mousedown", handleMouseDown);
      trigger.removeEventListener("mouseenter", handleMouseEnter);
      trigger.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    handleKeyDown,
    handleKeyUp,
    handleMouseDown,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseUp,
    trigger,
  ]);

  return { rippleProps };
}

let instances = 0;

export const Ripple: FC<RippleProps> = ({ coords, ...props }) => {
  const theme = useTheme();
  const maskId = useMemo(() => `ripple-mask-${++instances}`, []);

  const circle = (
    <Circle
      {...props}
      cx="50"
      cy="50"
      r={props.phase === "hold" ? "0" : "50"}
    />
  );

  return (
    <ContainerSvg
      {...props}
      aria-hidden
      viewBox="0 0 100 100"
      style={{ left: coords.x, top: coords.y }}
    >
      <BackgroundRect
        x="0"
        y="0"
        width="100"
        height="100"
        mask={
          theme.components.ripple.circleFill === "transparent"
            ? `url(#${maskId})`
            : undefined
        }
      />
      {theme.components.ripple.circleFill === "transparent" ? (
        <defs>
          <mask id={maskId} x="0" y="0" width="100" height="100">
            <rect x="0" y="0" width="100" height="100" fill="#fff" />
            {circle}
          </mask>
        </defs>
      ) : (
        circle
      )}
    </ContainerSvg>
  );
};

const BackgroundRect = styled.rect`
  fill: ${({ theme }) => tombac.parse(theme.components.ripple.fill)};
`;

const Circle = styled.circle<Pick<RippleProps, "phase">>`
  fill: ${({
    theme: {
      components: { ripple },
    },
  }) =>
    ripple.circleFill === "transparent"
      ? "#000"
      : tombac.parse(ripple.circleFill)};
  transform-origin: 50%;
  transition-property: r;
  transition-timing-function: ease-out;

  ${tombac.variant("phase", {
    $default: css`
      transition-duration: 0s;
    `,
    released: css`
      transition-duration: 0.3s;
    `,
  })}
`;

const ContainerSvg = styled.svg<Pick<RippleProps, "phase">>`
  opacity: 0;
  position: absolute;
  transform: translate(-50%, -50%);
  transition: opacity 0.1s ease-out;
  width: 200%;

  ${tombac.variant("phase", {
    canceled: css`
      opacity: 0;
    `,
    idle: css`
      opacity: 0;
      transition-duration: 0s;
    `,
    hold: css`
      opacity: 1;
    `,
    released: css`
      opacity: 0;
      transition-delay: 0.3s;
      transition-duration: 0.5s;
    `,
  })}
`;
